import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Bot,
  Workflow,
  BarChart3,
  Package,
  MessageSquare,
  Globe,
  Zap,
  ArrowRight,
  Check,
  ChevronRight,
  Settings,
  Brain,
  Layers,
  Database,
  Shield,
  Smartphone,
  LayoutDashboard,
  FileText,
  Bell,
  Calendar,
  Receipt,
  Users,
  Plug,
  Languages,
  Clock,
  TrendingUp,
  PieChart,
  Target,
  Lightbulb,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1, y: 0, filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* ─── HERO ─── */
const PlatformHero = () => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-violet-300/20 rounded-full blur-[120px] animate-float-orb" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300/15 rounded-full blur-[120px] animate-float-orb-reverse" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left — Text content */}
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.span variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm text-slate-600">
            <Layers className="w-4 h-4 text-violet-600" /> The BusiGrow Platform
          </motion.span>
          <motion.h1 variants={fadeUp} custom={1} className="mt-6 sm:mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]">
            One platform to{" "}
            <span className="text-gradient">automate everything</span>
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 max-w-2xl">
            AI Agents, workflow automation, business intelligence, and industry-specific packs —
            all in one place, built for how businesses actually operate in emerging markets.
          </motion.p>
        </motion.div>

        {/* Right — Hero image */}
        <motion.div
          initial={{ opacity: 0, x: 60, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          {/* Morphing blob decorative element */}
          <div className="absolute -inset-6 bg-gradient-to-br from-violet-400/20 via-purple-300/10 to-transparent rounded-full blur-3xl animate-float-orb pointer-events-none" />

          <img
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
            alt="BusiGrow platform overview"
            className="relative w-full rounded-3xl shadow-apple-xl animate-glow-pulse object-cover aspect-[4/3]"
            loading="lazy"
          />

          {/* Floating card overlay */}
          <div className="absolute bottom-2 left-2 sm:-bottom-6 sm:-left-6 glass-strong rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-apple-lg flex items-center gap-2 sm:gap-3 border border-violet-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">AI-Powered</p>
              <p className="text-xs text-slate-500">Intelligent automation</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── AI AGENT BUILDER ─── */
const agentCapabilities = [
  { icon: Brain, title: "Natural Language Understanding", desc: "Understands intent, context, and sentiment — not just keywords" },
  { icon: Languages, title: "25+ Languages", desc: "Hindi, Arabic, Tamil, Urdu, Bengali, English, and more out of the box" },
  { icon: Database, title: "Train on Your Data", desc: "Upload PDFs, product catalogs, past chats — your agent learns YOUR business" },
  { icon: Smartphone, title: "Multi-Channel Deploy", desc: "WhatsApp, website chat, Instagram DM, Telegram — one agent, everywhere" },
  { icon: Clock, title: "Sub-300ms Response", desc: "Lightning-fast responses that feel human, powered by optimized LLM inference" },
  { icon: Shield, title: "Guardrails Built-In", desc: "Content filters, escalation rules, and human handoff when needed" },
];

const AIAgentSection = () => (
  <section id="agents" className="py-16 sm:py-24 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-600 text-sm font-semibold">
            <Bot className="w-4 h-4" /> Core Feature
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            AI Agent Builder
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="mt-4 text-base sm:text-lg text-slate-500 leading-relaxed">
            Build intelligent AI agents that handle customer support, qualify leads, take orders,
            and manage operations — without writing a single line of code. Train them on your
            business data and deploy everywhere your customers are.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {agentCapabilities.map((cap, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <cap.icon className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{cap.title}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">{cap.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="rounded-2xl glass-strong border border-violet-100 overflow-hidden shadow-apple card-shine">
            <img
              src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80"
              alt="AI Agent Builder Interface"
              className="w-full aspect-[4/3] object-cover img-parallax"
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── WORKFLOW AUTOMATION ─── */
const workflowTemplates = [
  { icon: Receipt, name: "Invoice Processing", desc: "Auto-generate GST invoices from orders" },
  { icon: Bell, name: "Follow-up Sequences", desc: "Never miss a lead with smart reminders" },
  { icon: Calendar, name: "Appointment Booking", desc: "Self-service scheduling via WhatsApp" },
  { icon: Package, name: "Order Management", desc: "From order to delivery, fully automated" },
  { icon: FileText, name: "Document Workflows", desc: "Auto-create proposals, contracts, reports" },
  { icon: Users, name: "HR Onboarding", desc: "New employee setup in minutes, not days" },
];

const WorkflowSection = () => (
  <section id="workflows" className="py-16 sm:py-24 scroll-mt-20 bg-violet-tint">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="rounded-2xl glass-strong border border-violet-100 overflow-hidden shadow-apple card-shine">
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
              alt="Visual Workflow Editor"
              className="w-full aspect-[4/3] object-cover img-parallax"
              loading="lazy"
            />
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-600 text-sm font-semibold">
            <Workflow className="w-4 h-4" /> Core Feature
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            Workflow Automation Engine
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="mt-4 text-base sm:text-lg text-slate-500 leading-relaxed">
            Visual drag-and-drop builder to automate any business process.
            Connect your existing tools — Tally, Zoho, Google Sheets, WhatsApp Business API —
            and let automation handle the repetitive work.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {workflowTemplates.map((t, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl glass hover:border-violet-200 hover:shadow-apple transition-all">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <t.icon className="w-4.5 h-4.5 text-violet-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{t.name}</h4>
                  <p className="text-xs text-slate-400">{t.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── BUSINESS INTELLIGENCE ─── */
const biFeatures = [
  { icon: TrendingUp, title: "Real-Time Metrics", desc: "Live dashboards connected to all your tools and channels" },
  { icon: Lightbulb, title: "AI-Generated Insights", desc: "\"Your Tuesday sales are 40% higher — run promos on slow days\"" },
  { icon: PieChart, title: "Revenue Forecasting", desc: "Predictive models trained on your data with regional seasonality" },
  { icon: Target, title: "Custom Reports", desc: "One-click reports for any metric. Export to PDF, Excel, or share live links" },
];

const BISection = () => (
  <section id="analytics" className="py-16 sm:py-24 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center max-w-3xl mx-auto">
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-600 text-sm font-semibold mx-auto">
          <BarChart3 className="w-4 h-4" /> Core Feature
        </motion.div>
        <motion.h2 variants={fadeUp} custom={1} className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
          Business Intelligence Dashboard
        </motion.h2>
        <motion.p variants={fadeUp} custom={2} className="mt-4 text-base sm:text-lg text-slate-500">
          Stop guessing. See every metric that matters in real time, with AI telling you what
          to do next. Connected to every tool in your stack.
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mt-12 rounded-2xl glass-strong border border-violet-100 p-2 shadow-apple-xl max-w-5xl mx-auto"
      >
        <div className="rounded-xl overflow-hidden aspect-[16/9] card-shine">
          <img
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80"
            alt="Analytics Dashboard with charts, KPIs, and AI insights"
            className="w-full h-full object-cover img-parallax"
            loading="lazy"
          />
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {biFeatures.map((f, i) => (
          <motion.div key={i} variants={fadeUp} custom={i} className="text-center p-6 rounded-2xl glass shadow-apple">
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center mx-auto">
              <f.icon className="w-6 h-6 text-violet-600" />
            </div>
            <h3 className="mt-4 text-base font-bold text-slate-900">{f.title}</h3>
            <p className="mt-2 text-sm text-slate-500">{f.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── INDUSTRY PACKS ─── */
const packs = [
  {
    name: "Retail & E-commerce", icon: "🛍️",
    features: ["WhatsApp catalog & ordering", "Inventory level alerts", "Customer re-engagement automation", "GST invoice generation", "Delivery tracking notifications", "Abandoned cart recovery"],
  },
  {
    name: "Healthcare Clinics", icon: "🏥",
    features: ["Appointment booking via WhatsApp", "Automated patient reminders", "Prescription & report delivery", "Queue management", "Follow-up scheduling", "Insurance verification workflows"],
  },
  {
    name: "Real Estate", icon: "🏗️",
    features: ["Lead qualification chatbot", "Property matching engine", "Automated site visit scheduling", "Document collection workflows", "Payment milestone tracking", "Post-sale support automation"],
  },
  {
    name: "Restaurant & F&B", icon: "🍽️",
    features: ["WhatsApp ordering system", "Table reservation management", "Kitchen order routing", "Customer feedback collection", "Loyalty program automation", "Supplier order workflows"],
  },
  {
    name: "Education", icon: "📚",
    features: ["Enrollment & admission workflows", "Fee payment reminders", "Parent-teacher communication", "Assignment submission tracking", "Attendance notifications", "Exam schedule management"],
  },
  {
    name: "Professional Services", icon: "⚖️",
    features: ["Client intake automation", "Document generation (contracts, proposals)", "Billing & invoicing workflows", "Deadline & compliance tracking", "Client portal via WhatsApp", "Case/project status updates"],
  },
];

const IndustryPacksSection = () => {
  const [activePack, setActivePack] = useState(0);

  return (
    <section id="industry-packs" className="relative py-16 sm:py-24 scroll-mt-20 bg-violet-tint overflow-hidden">
      {/* Decorative floating orbs */}
      <div className="absolute top-16 left-10 w-72 h-72 bg-violet-300/15 rounded-full blur-[100px] animate-float-orb pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-64 h-64 bg-purple-300/10 rounded-full blur-[100px] animate-float-orb-reverse pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-fuchsia-200/10 rounded-full blur-[120px] animate-float-orb pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center max-w-3xl mx-auto">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-600 text-sm font-semibold mx-auto">
            <Package className="w-4 h-4" /> Vertical Solutions
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            Industry Packs
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="mt-4 text-base sm:text-lg text-slate-500">
            Pre-configured automation templates, AI agent personas, and workflows —
            tailored for your specific industry. Get started in minutes.
          </motion.p>
        </motion.div>

        <div className="mt-14">
          <div className="scroll-pills sm:flex sm:flex-wrap justify-center sm:gap-2 mb-8">
            {packs.map((pack, i) => (
              <button
                key={i}
                onClick={() => setActivePack(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activePack === i
                    ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-apple-lg"
                    : "glass text-slate-600 hover:border-violet-200"
                }`}
              >
                <span>{pack.icon}</span>
                <span className="hidden sm:inline">{pack.name}</span>
              </button>
            ))}
          </div>

          <motion.div
            key={activePack}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
          >
            <div className="p-8 rounded-2xl glass-strong shadow-apple-lg">
              <span className="text-4xl">{packs[activePack].icon}</span>
              <h3 className="mt-4 text-2xl font-bold text-slate-900">{packs[activePack].name} Pack</h3>
              <p className="mt-2 text-slate-500">Everything your {packs[activePack].name.toLowerCase()} business needs to automate operations.</p>
              <ul className="mt-6 space-y-3">
                {packs[activePack].features.map((f, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <span className="text-slate-600">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg transition-all"
              >
                Get This Pack <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="rounded-2xl glass-strong border border-violet-100 overflow-hidden shadow-apple aspect-[4/3] relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
                alt={`${packs[activePack].name} dashboard`}
                className="w-full h-full object-cover img-parallax"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent flex flex-col items-center justify-end pb-6">
                <span className="text-4xl block mb-2">{packs[activePack].icon}</span>
                <p className="text-slate-700 font-semibold">{packs[activePack].name} Pack</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─── INTEGRATIONS ─── */
const integrations = [
  "WhatsApp Business API", "Tally ERP", "Zoho CRM", "Google Sheets",
  "Google Calendar", "Razorpay", "Stripe", "Gmail",
  "Slack", "Instagram", "Telegram", "Shopify",
];

const IntegrationsSection = () => (
  <section id="integrations" className="py-16 sm:py-24 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-slate-600 text-sm font-semibold">
          <Plug className="w-4 h-4 text-violet-600" /> Integrations
        </motion.div>
        <motion.h2 variants={fadeUp} custom={1} className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900">
          Connects to everything you already use
        </motion.h2>
        <motion.p variants={fadeUp} custom={2} className="mt-4 text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
          BusiGrow plugs into your existing stack. No rip-and-replace. Just instant automation on top.
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 animate-glow-pulse"
      >
        {integrations.map((name, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i}
            className="p-4 rounded-xl glass hover:shadow-apple hover:border-violet-200 transition-all flex flex-col items-center gap-2"
          >
            <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
              <Plug className="w-5 h-5 text-violet-400" />
            </div>
            <span className="text-xs font-medium text-slate-600 text-center">{name}</span>
          </motion.div>
        ))}
      </motion.div>

      <p className="mt-8 text-sm text-slate-400">
        Don't see your tool? <Link to="/contact" className="text-violet-600 font-medium hover:underline">Request an integration</Link>
      </p>
    </div>
  </section>
);

/* ─── PAGE ─── */
const PlatformPage = () => (
  <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
    <Header />
    <PlatformHero />
    <AIAgentSection />
    <WorkflowSection />
    <BISection />
    <IndustryPacksSection />
    <IntegrationsSection />
    <Footer />
  </div>
);

export default PlatformPage;
